import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  Plus, User, Calendar, MapPin, Clock, DollarSign, 
  Star, Eye, Edit, LogOut, Bell, Settings, Search,
  CheckCircle, AlertCircle, Package, FileText, MessageCircle
} from 'lucide-react';
import { useCustomerAuth, customerApi } from '../../contexts/CustomerAuthContext';
import CustomerHeader from './CustomerHeader';
import Footer from '../public/Footer';
import { normalizeImageUrl } from '../../utils/api';
import './CustomerDashboard.css';

interface Booking {
  id: number;
  title: string;
  talent_name: string;
  date: string;
  time: string;
  location: string;
  price: number;
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled';
  rating?: number;
  talent_id: number;
}

interface ServiceInquiry {
  id: number;
  inquiry_reference: string;
  title: string;
  description: string;
  category_name: string;
  skill_name?: string;
  location_address: string;
  scheduled_date: string;
  scheduled_time: string;
  budget_min?: number;
  budget_max?: number;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'new' | 'assigned' | 'in_progress' | 'completed' | 'cancelled';
  created_at: string;
}

interface Conversation {
  other_party_id: number;
  other_party_type: string;
  first_name: string;
  last_name: string;
  profile_picture: string | null;
  last_message_time: string;
  unread_count: number;
}

interface CustomerProfile {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  address?: string;
  parish?: string;
  subscription_status: 'free' | 'premium';
  total_bookings: number;
  completed_bookings: number;
  created_at: string;
}

const CustomerDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [inquiries, setInquiries] = useState<ServiceInquiry[]>([]);
  const [recentConversations, setRecentConversations] = useState<Conversation[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [profile, setProfile] = useState<CustomerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [bookingFilter, setBookingFilter] = useState('all');
  const [inquiryFilter, setInquiryFilter] = useState('all');
  
  // Edit profile states
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    parish: ''
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileMessage, setProfileMessage] = useState({ type: '', text: '' });

  const { customer, logout, isAuthenticated } = useCustomerAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchData();
  }, []);

  // Read URL hash to activate the right tab (e.g. #profile from Profile Settings link)
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    const validTabs = ['overview', 'inquiries', 'profile'];
    if (validTabs.includes(hash)) {
      setActiveTab(hash);
    }
  }, [location.hash]);

  const fetchData = async () => {
    try {
      setLoading(true);
      await Promise.all([fetchProfile(), fetchBookings(), fetchInquiries(), fetchRecentConversations()]);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await customerApi.get('/customers/profile');
      if (response.data.success && response.data.customer) {
        const customerData = response.data.customer;
        // Ensure subscription_status matches the expected type
        const normalizedCustomer: CustomerProfile = {
          ...customerData,
          subscription_status: customerData.subscription_status === 'premium' ? 'premium' : 'free'
        };
        setProfile(normalizedCustomer);
        // Initialize form with current profile data
        setProfileForm({
          first_name: customerData.first_name || '',
          last_name: customerData.last_name || '',
          email: customerData.email || '',
          phone: customerData.phone || '',
          address: customerData.address || '',
          parish: customerData.parish || ''
        });
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await customerApi.get('/customers/my-bookings');
      if (response.data.success) {
        setBookings(response.data.bookings || []);
      }
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    }
  };

  const fetchInquiries = async () => {
    try {
      const response = await customerApi.get('/customers/my-inquiries');
      if (response.data.success) {
        setInquiries(response.data.inquiries || []);
      }
    } catch (error) {
      console.error('Failed to fetch inquiries:', error);
    }
  };

  const fetchRecentConversations = async () => {
    try {
      setMessagesLoading(true);
      const response = await customerApi.get('/messages/conversations?limit=5&page=1');
      if (response.data.success) {
        setRecentConversations(response.data.conversations || []);
      }
    } catch (error) {
      console.error('Failed to fetch recent conversations:', error);
    } finally {
      setMessagesLoading(false);
    }
  };

  const getProfileImage = (profilePicture: string | null) => {
    return normalizeImageUrl(profilePicture, '/assets/images/default-avatar.png');
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleEditProfile = () => {
    setIsEditingProfile(true);
    setProfileMessage({ type: '', text: '' });
  };

  const handleCancelEdit = () => {
    setIsEditingProfile(false);
    // Reset form to current profile data
    if (profile) {
      setProfileForm({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        email: profile.email || '',
        phone: profile.phone || '',
        address: profile.address || '',
        parish: profile.parish || ''
      });
    }
    setProfileMessage({ type: '', text: '' });
  };

  const handleProfileFormChange = (field: string, value: string) => {
    setProfileForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveProfile = async () => {
    try {
      setProfileLoading(true);
      setProfileMessage({ type: '', text: '' });

      const response = await customerApi.put('/customers/profile', profileForm);
      
      if (response.data.success) {
        setProfileMessage({ type: 'success', text: 'Profile updated successfully!' });
        setIsEditingProfile(false);
        // Refresh profile data
        await fetchProfile();
      } else {
        setProfileMessage({ type: 'error', text: response.data.message || 'Failed to update profile' });
      }
    } catch (error: any) {
      console.error('Failed to update profile:', error);
      setProfileMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to update profile. Please try again.' 
      });
    } finally {
      setProfileLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': 
      case 'new': return '#f59e0b';
      case 'confirmed':
      case 'assigned': return '#10b981';
      case 'in_progress': return '#6366f1';
      case 'completed': return '#3b82f6';
      case 'cancelled': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'Pending';
      case 'new': return 'New';
      case 'confirmed': return 'Confirmed';
      case 'assigned': return 'Assigned';
      case 'in_progress': return 'In Progress';
      case 'completed': return 'Completed';
      case 'cancelled': return 'Cancelled';
      default: return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const filteredBookings = bookings.filter(booking => {
    if (bookingFilter === 'all') return true;
    return booking.status === bookingFilter;
  });

  const filteredInquiries = inquiries.filter(inquiry => {
    if (inquiryFilter === 'all') return true;
    return inquiry.status === inquiryFilter;
  });

  const stats = {
    totalInquiries: inquiries.length,
    newInquiries: inquiries.filter(i => i.status === 'new').length,
    inProgressInquiries: inquiries.filter(i => i.status === 'in_progress').length,
    completedInquiries: inquiries.filter(i => i.status === 'completed').length
  };

  if (loading) {
    return (
      <>
        <CustomerHeader />
        <div className="dashboard-loading">
          <div className="loading-spinner" />
          <p>Loading dashboard...</p>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <CustomerHeader />
      <div className="customer-dashboard">
        <div className="dashboard-welcome">
          <div className="container">
            <div className="welcome-content">
              <h1>Welcome back, {customer?.firstName || 'Customer'}!</h1>
              <p>Manage your service inquiries and find skilled professionals</p>
            </div>
          </div>
        </div>

        <div className="dashboard-navigation">
          <div className="container">
            <nav className="dashboard-nav">
              <button 
                className={activeTab === 'overview' ? 'active' : ''}
                onClick={() => setActiveTab('overview')}
              >
                <Package size={18} />
                Overview
              </button>
              
              <button 
                className={activeTab === 'inquiries' ? 'active' : ''}
                onClick={() => setActiveTab('inquiries')}
              >
                <FileText size={18} />
                Service Inquiries
              </button>
            
              <button 
                className={activeTab === 'profile' ? 'active' : ''}
                onClick={() => setActiveTab('profile')}
              >
                <User size={18} />
                Profile
              </button>
            </nav>
          </div>
        </div>

        <div className="dashboard-content">
          <div className="container">
            {activeTab === 'overview' && (
              <div className="overview-tab">
                <div className="stats-grid">
                  <div className="stat-card">
                    <div className="stat-icon">
                      <Calendar size={24} />
                    </div>
                    <div className="stat-info">
                      <h3>{stats.totalInquiries}</h3>
                      <p>Total Inquiries</p>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon">
                      <CheckCircle size={24} />
                    </div>
                    <div className="stat-info">
                      <h3>{stats.completedInquiries}</h3>
                      <p>Completed</p>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon">
                      <Clock size={24} />
                    </div>
                    <div className="stat-info">
                      <h3>{stats.newInquiries}</h3>
                      <p>New</p>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon">
                      <DollarSign size={24} />
                    </div>
                    <div className="stat-info">
                      <h3>{stats.inProgressInquiries}</h3>
                      <p>In Progress</p>
                    </div>
                  </div>
                </div>

                <div className="overview-actions">
                  {/* <Link to="/customer/service-inquiry" className="cm-btn">
                    <Plus size={20} />
                    Request Service
                  </Link> */}
                  <Link to="/customer/messages" className="cm-btn">
                    <MessageCircle size={20} />
                    My Messages
                  </Link>
                  <Link to="/search" className='cm-btn'>
                    <Search size={20} />
                    Find Talent
                  </Link>
                </div>

                <div className="recent-inquiries container mt-4">
                  <h3 className="mb-4">Recent Messages</h3>
                  {messagesLoading ? (
                    <div className="text-center py-3">
                      <div className="loading-spinner" />
                    </div>
                  ) : recentConversations.length === 0 ? (
                    <div className="alert alert-info text-center">
                      No messages yet. Start a conversation by contacting a service provider.
                    </div>
                  ) : (
                    <div className="list-group">
                      {recentConversations.map((c) => (
                        <div key={`${c.other_party_type}-${c.other_party_id}`} className="list-group-item d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center gap-3">
                            <img
                              src={getProfileImage(c.profile_picture)}
                              alt={`${c.first_name} ${c.last_name}`}
                              width={40}
                              height={40}
                              style={{ borderRadius: '50%', objectFit: 'cover' }}
                              onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/assets/images/default-avatar.png'; }}
                            />
                            <div>
                              <div className="fw-semibold">
                                {c.first_name} {c.last_name}
                                {c.unread_count > 0 && (
                                  <span className="badge bg-primary ms-2">{c.unread_count}</span>
                                )}
                              </div>
                              <small className="text-muted">{new Date(c.last_message_time).toLocaleString()}</small>
                            </div>
                          </div>
                          <Link to={`/customer/messages?otherId=${c.other_party_id}`} className="btn btn-sm btn-outline-primary">
                            <MessageCircle size={14} className="me-1" /> Open
                          </Link>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

              </div>
            )}

            {activeTab === 'inquiries' && (
              <div className="inquiries-tab">
                <div className="inquiries-header">
                  <h3>My Service Inquiries</h3>
                  <div className="inquiries-filters">
                    <select 
                      value={inquiryFilter} 
                      onChange={(e) => setInquiryFilter(e.target.value)}
                    >
                      <option value="all">All Inquiries</option>
                      <option value="new">New</option>
                      <option value="assigned">Assigned</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>
                
                {filteredInquiries.length === 0 ? (
                  <div className="alert alert-info text-center">
                    <p>No inquiries found for the selected filter.</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead className="table-light">
                        <tr>
                          <th scope="col">Reference</th>
                          <th scope="col">Service</th>
                          <th scope="col">Category</th>
                          <th scope="col">Date & Time</th>
                          <th scope="col">Location</th>
                          <th scope="col">Priority</th>
                          <th scope="col">Budget</th>
                          <th scope="col">Status</th>
                          <th scope="col">Created</th>
                          <th scope="col">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredInquiries.map((inquiry) => (
                          <tr key={inquiry.id}>
                            <td>
                              <span className="fw-bold text-primary">{inquiry.inquiry_reference}</span>
                            </td>
                            <td>
                              <div>
                                <div className="fw-semibold">{inquiry.title}</div>
                                {inquiry.skill_name && (
                                  <small className="text-muted">{inquiry.skill_name}</small>
                                )}
                                {inquiry.description && (
                                  <div className="mt-1">
                                    <small className="text-muted">
                                      {inquiry.description.length > 50 
                                        ? `${inquiry.description.substring(0, 50)}...`
                                        : inquiry.description
                                      }
                                    </small>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>
                              <span className="badge bg-light text-dark">{inquiry.category_name}</span>
                            </td>
                            <td>
                              <div className="d-flex flex-column">
                                <span className="d-flex align-items-center mb-1">
                                  <Calendar size={12} className="me-1 text-secondary" />
                                  <small>{new Date(inquiry.scheduled_date).toLocaleDateString()}</small>
                                </span>
                                <span className="d-flex align-items-center">
                                  <Clock size={12} className="me-1 text-secondary" />
                                  <small>{inquiry.scheduled_time}</small>
                                </span>
                              </div>
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <MapPin size={12} className="me-1 text-secondary" />
                                <small>{inquiry.location_address}</small>
                              </div>
                            </td>
                            <td>
                              <span className={`badge ${
                                inquiry.priority === 'urgent' ? 'bg-danger' :
                                inquiry.priority === 'high' ? 'bg-warning' :
                                inquiry.priority === 'medium' ? 'bg-info' :
                                'bg-secondary'
                              }`}>
                                {inquiry.priority.charAt(0).toUpperCase() + inquiry.priority.slice(1)}
                              </span>
                            </td>
                            <td>
                              {(inquiry.budget_min || inquiry.budget_max) ? (
                                <span className="fw-bold text-success">
                                  {inquiry.budget_min && inquiry.budget_max
                                    ? `$${inquiry.budget_min} - $${inquiry.budget_max}`
                                    : inquiry.budget_min
                                    ? `$${inquiry.budget_min}+`
                                    : `Up to $${inquiry.budget_max}`}
                                </span>
                              ) : (
                                <span className="text-muted">Not specified</span>
                              )}
                            </td>
                            <td>
                              <span
                                className="badge text-white px-2 py-1"
                                style={{ backgroundColor: getStatusColor(inquiry.status) }}
                              >
                                {getStatusText(inquiry.status)}
                              </span>
                            </td>
                            <td>
                              <small className="text-muted">
                                {new Date(inquiry.created_at).toLocaleDateString()}
                              </small>
                            </td>
                            <td>
                              <button 
                                className="btn btn-sm btn-outline-primary"
                                title="View Details"
                              >
                                <Eye size={14} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'profile' && (
              <div className="profile-tab">
                <div className="profile-header">
                  <h3>Profile Information</h3>
                  {!isEditingProfile ? (
                    <button
                      onClick={handleEditProfile}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '9px 18px',
                        background: '#185FA5',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        width: 'auto',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.2s ease',
                      }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#0C467E'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#185FA5'; }}
                    >
                      <Edit size={16} />
                      Edit Profile
                    </button>
                  ) : (
                    <div className="d-flex gap-2">
                      <button
                        onClick={handleSaveProfile}
                        disabled={profileLoading}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '9px 20px',
                          background: profileLoading ? '#6b7280' : '#185FA5',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontWeight: 700,
                          fontSize: '14px',
                          cursor: profileLoading ? 'not-allowed' : 'pointer',
                          transition: 'all 0.2s ease',
                        }}
                        onMouseEnter={(e) => { if (!profileLoading) (e.currentTarget as HTMLButtonElement).style.background = '#0C467E'; }}
                        onMouseLeave={(e) => { if (!profileLoading) (e.currentTarget as HTMLButtonElement).style.background = '#185FA5'; }}
                      >
                        {profileLoading ? 'Saving...' : 'Save Changes'}
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        disabled={profileLoading}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '9px 20px',
                          background: 'white',
                          color: '#185FA5',
                          border: '2px solid #185FA5',
                          borderRadius: '8px',
                          fontWeight: 600,
                          fontSize: '14px',
                          cursor: profileLoading ? 'not-allowed' : 'pointer',
                          transition: 'all 0.2s ease',
                        }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#E6F1FB'; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'white'; }}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>

                {profileMessage.text && (
                  <div className={`alert alert-${profileMessage.type === 'success' ? 'success' : 'danger'} mt-3`}>
                    {profileMessage.text}
                  </div>
                )}
                
                {profile && (
                  <div className="customer-profile-info">
                    <div className="customer-profile-section">
                      <h4>Personal Information</h4>
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label">First Name</label>
                          {isEditingProfile ? (
                            <input
                              type="text"
                              className="form-control"
                              value={profileForm.first_name}
                              onChange={(e) => handleProfileFormChange('first_name', e.target.value)}
                            />
                          ) : (
                            <div className="form-control-plaintext">{profile.first_name}</div>
                          )}
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Last Name</label>
                          {isEditingProfile ? (
                            <input
                              type="text"
                              className="form-control"
                              value={profileForm.last_name}
                              onChange={(e) => handleProfileFormChange('last_name', e.target.value)}
                            />
                          ) : (
                            <div className="form-control-plaintext">{profile.last_name}</div>
                          )}
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Email</label>
                          {isEditingProfile ? (
                            <input
                              type="email"
                              className="form-control"
                              value={profileForm.email}
                              onChange={(e) => handleProfileFormChange('email', e.target.value)}
                            />
                          ) : (
                            <div className="form-control-plaintext">{profile.email}</div>
                          )}
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Phone</label>
                          {isEditingProfile ? (
                            <input
                              type="tel"
                              className="form-control"
                              value={profileForm.phone}
                              onChange={(e) => handleProfileFormChange('phone', e.target.value)}
                              placeholder="Enter phone number"
                            />
                          ) : (
                            <div className="form-control-plaintext">{profile.phone || 'Not provided'}</div>
                          )}
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Parish/City</label>
                          {isEditingProfile ? (
                            <input
                              type="text"
                              className="form-control"
                              value={profileForm.parish}
                              onChange={(e) => handleProfileFormChange('parish', e.target.value)}
                              placeholder="Enter parish or city"
                            />
                          ) : (
                            <div className="form-control-plaintext">{profile.parish || 'Not provided'}</div>
                          )}
                        </div>
                        <div className="col-12">
                          <label className="form-label">Address</label>
                          {isEditingProfile ? (
                            <textarea
                              className="form-control"
                              rows={3}
                              value={profileForm.address}
                              onChange={(e) => handleProfileFormChange('address', e.target.value)}
                              placeholder="Enter full address"
                            />
                          ) : (
                            <div className="form-control-plaintext">{profile.address || 'Not provided'}</div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="customer-profile-section">
                      <h4>Account Information</h4>
                      <div className="row g-3">
                        <div className="col-md-4">
                          <label className="form-label">Subscription</label>
                          <div className="form-control-plaintext">
                            <span className={`badge ${profile.subscription_status === 'premium' ? 'bg-warning text-dark' : 'bg-secondary'}`}>
                              {profile.subscription_status === 'premium' ? 'Premium' : 'Free'}
                            </span>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">Member Since</label>
                          <div className="form-control-plaintext">
                            {new Date(profile.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">Completed Bookings</label>
                          <div className="form-control-plaintext">
                            <span className="badge bg-success">{profile.completed_bookings}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default CustomerDashboard;